import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  duration_minutes: z.number().min(1, "Duration must be at least 1 minute").max(1440, "Duration cannot exceed 24 hours"),
  requires_approval: z.boolean().default(true),
  allowed_commands: z.string().optional(),
});

interface CreatePrivilegeRuleFormProps {
  onSuccess?: () => void;
}

export function CreatePrivilegeRuleForm({ onSuccess }: CreatePrivilegeRuleFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      duration_minutes: 30,
      requires_approval: true,
      allowed_commands: "",
    },
  });

  const generateAdminScript = (durationMinutes: number) => {
    return `#!/bin/bash

# Script to grant temporary admin privileges
# Based on MakeMeAnAdmin by JAMF Software

# Configuration
DURATION_MINUTES=${durationMinutes}
ADMIN_GROUP="admin"
SECURITY_GROUP="security"

# Get the current console user
CONSOLE_USER=$(stat -f%Su /dev/console)
if [ -z "$CONSOLE_USER" ]; then
    echo "No console user found"
    exit 1
fi

# Check if user is already an admin
if dseditgroup -o checkmember -m "$CONSOLE_USER" "$ADMIN_GROUP" > /dev/null 2>&1; then
    echo "User $CONSOLE_USER is already an admin"
    exit 0
fi

# Add user to admin group
/usr/sbin/dseditgroup -o edit -a "$CONSOLE_USER" -t user "$ADMIN_GROUP"
if [ $? -ne 0 ]; then
    echo "Failed to add user to admin group"
    exit 1
fi

echo "Added $CONSOLE_USER to admin group"

# Schedule removal of admin rights
cat << EOF > "/private/tmp/remove_admin_$CONSOLE_USER.sh"
#!/bin/bash
/usr/sbin/dseditgroup -o edit -d "$CONSOLE_USER" -t user "$ADMIN_GROUP"
rm -f "/private/tmp/remove_admin_$CONSOLE_USER.sh"
EOF

chmod +x "/private/tmp/remove_admin_$CONSOLE_USER.sh"

# Schedule the removal using at command
echo "/private/tmp/remove_admin_$CONSOLE_USER.sh" | at now + $DURATION_MINUTES minutes 2>/dev/null

echo "Admin privileges will be removed in $DURATION_MINUTES minutes"
exit 0`;
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session?.user) {
        toast.error("You must be logged in to create rules");
        return;
      }

      // First create the configuration with the admin script
      const { data: configData, error: configError } = await supabase
        .from("configurations")
        .insert({
          name: `Admin Script - ${values.name}`,
          description: `Temporary admin privileges script for rule: ${values.name}`,
          script_content: generateAdminScript(values.duration_minutes),
          script_type: "shell",
          user_id: session.session.user.id,
        })
        .select()
        .single();

      if (configError) {
        console.error("Error creating configuration:", configError);
        throw configError;
      }

      // Then create the privilege elevation rule
      const { data, error } = await supabase
        .from("privilege_elevation_rules")
        .insert({
          name: values.name,
          description: values.description,
          duration_minutes: values.duration_minutes,
          requires_approval: values.requires_approval,
          allowed_commands: values.allowed_commands
            ? values.allowed_commands.split(",").map((cmd) => cmd.trim())
            : [],
          user_id: session.session.user.id,
          configuration_id: configData.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success("Rule created successfully");
      form.reset();
      onSuccess?.();
    } catch (error) {
      console.error("Error creating rule:", error);
      toast.error("Failed to create rule");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter rule name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Enter rule description"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="duration_minutes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Duration (minutes)</FormLabel>
              <FormControl>
                <Input 
                  type="number"
                  min={1}
                  max={1440}
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                />
              </FormControl>
              <FormDescription>
                Specify how long the elevated privileges should last (max 24 hours)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="requires_approval"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Require Approval
                </FormLabel>
                <FormDescription>
                  When enabled, requests must be approved by an administrator
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="allowed_commands"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Allowed Commands</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter comma-separated list of allowed commands"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Optional: List specific commands that are allowed during elevation
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Rule"}
          </Button>
        </div>
      </form>
    </Form>
  );
}